import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapPin, Bus, AlertTriangle, Bell, RefreshCw, CheckCircle, Clock,
         Wifi, WifiOff, Navigation, BarChart2, TrendingUp, Route } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { api } from '../../utils/api';
import { formatDateTime } from '../../utils/helpers';

const REFRESH_INTERVAL = 15000;

// ── helpers ───────────────────────────────────────────────────
const fmtKm  = v => v != null ? `${parseFloat(v).toFixed(1)} km` : '—';
const fmtMin = v => {
  if (v == null) return '—';
  const m = Math.round(v);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m/60)}h ${m%60}m`;
};
const fmtDate = s => { if (!s) return '—'; const d = new Date(s); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; };
const fmtTime = s => {
  if (!s) return '—';
  try { return new Date(s).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true}); }
  catch { return s; }
};
const ageMin = ts => Math.round((Date.now() - new Date(ts.replace(' ','T')).getTime()) / 60000);

// ── Embedded Bus Map (Leaflet via CDN) ───────────────────────
function BusMap({ positions, selectedRoute }) {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const markersRef   = useRef([]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const L = window.L;
    if (!L) return;
    const map = L.map(containerRef.current, { zoomControl: true }).setView([17.3850, 78.4867], 10);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap', maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const L = window.L;
    const map = mapRef.current;
    if (!L || !map) return;
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    const filtered = selectedRoute === 'all' ? positions : positions.filter(p => p.routeId === selectedRoute);
    const validPos  = filtered.filter(p => p.latitude && p.longitude);
    if (!validPos.length) return;

    const busIcon = L.divIcon({
      className: '',
      html: `<div style="background:#7c3aed;border:3px solid #fff;border-radius:50%;width:38px;height:38px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(124,58,237,.5);font-size:20px;">🚌</div>`,
      iconSize: [38,38], iconAnchor: [19,19],
    });

    validPos.forEach(pos => {
      const lat = parseFloat(pos.latitude);
      const lng = parseFloat(pos.longitude);
      if (isNaN(lat)||isNaN(lng)) return;
      const age  = ageMin(pos.recordedAt || pos.recorded_at || '');
      const live = age < 10;
      const marker = L.marker([lat,lng],{icon:busIcon}).addTo(map);
      marker.bindPopup(`
        <div style="min-width:170px;font-family:sans-serif">
          <div style="font-weight:700;font-size:13px;margin-bottom:4px">${pos.routeName||pos.routeId}</div>
          <div style="color:#6b7280;font-size:12px">👤 ${pos.driverName||'—'}</div>
          <div style="color:${live?'#16a34a':'#dc2626'};font-size:11px;margin-top:4px">${live?'🟢 Live':`🔴 ${age}m ago`}</div>
          ${pos.speed>0?`<div style="color:#6b7280;font-size:11px">💨 ${(pos.speed*3.6).toFixed(0)} km/h</div>`:''}
        </div>`);
      markersRef.current.push(marker);
    });

    if (markersRef.current.length) {
      try { map.fitBounds(L.featureGroup(markersRef.current).getBounds().pad(0.3),{maxZoom:15}); } catch(_){}
    }
  }, [positions, selectedRoute]);

  return (
    <div className="relative bg-gray-100" style={{height:360}}>
      <div ref={containerRef} style={{height:'100%',width:'100%',zIndex:0}}/>
      {!positions.length && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 text-gray-400 z-10">
          <Bus size={36} className="mb-2 opacity-30"/>
          <p className="text-sm font-medium">No buses active</p>
          <p className="text-xs mt-1">Map updates automatically</p>
        </div>
      )}
    </div>
  );
}

// ── Alert modal ───────────────────────────────────────────────
function AlertModal({ routes, onSend, onClose }) {
  const [form, setForm] = useState({ routeId:'', alertType:'delay', message:'' });
  const [sending, setSending] = useState(false);
  const tpl = {
    delay:'Bus is delayed by approximately 15 minutes due to traffic.',
    emergency:'Emergency situation on the bus. Students are safe. Please contact school.',
    breakdown:'Bus has broken down. Alternate transport is being arranged.',
    route_change:'Route has been changed today. Please check the updated route.',
  };
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <AlertTriangle size={20} className="text-red-500"/> Send Alert to Parents
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Route</label>
            <select value={form.routeId} onChange={e=>set('routeId',e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-300 focus:outline-none">
              <option value="">— Select route —</option>
              {routes.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Alert Type</label>
            <select value={form.alertType}
              onChange={e=>{set('alertType',e.target.value);set('message',tpl[e.target.value]||'');}}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-300 focus:outline-none">
              <option value="delay">Delay</option>
              <option value="breakdown">Breakdown</option>
              <option value="route_change">Route Change</option>
              <option value="emergency">Emergency</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Message</label>
            <textarea value={form.message} onChange={e=>set('message',e.target.value)} rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-300 focus:outline-none resize-none"/>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={async()=>{if(!form.routeId||!form.message.trim())return;setSending(true);await onSend(form);setSending(false);onClose();}}
            disabled={sending||!form.routeId||!form.message.trim()}
            className="px-5 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-60">
            {sending?'Sending…':'📤 Send Alert'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Analytics Tab ─────────────────────────────────────────────
function AnalyticsTab({ routes }) {
  const today = new Date().toISOString().split('T')[0];
  const [from, setFrom]         = useState(today);
  const [to, setTo]             = useState(today);
  const [routeId, setRouteId]   = useState('');
  const [data, setData]         = useState(null);
  const [stops, setStops]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [stopDate, setStopDate] = useState(today);
  const [stopRoute, setStopRoute] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from, to, ...(routeId ? { routeId } : {}) };
      const [res, stRes] = await Promise.all([
        api.list('transport/analytics', params),
        api.list('transport/analytics/stops', { date: stopDate, ...(stopRoute ? { routeId: stopRoute } : {}) }),
      ]);
      setData(res);
      setStops(Array.isArray(stRes) ? stRes : []);
    } catch {}
    setLoading(false);
  }, [from, to, routeId, stopDate, stopRoute]);

  useEffect(() => { load(); }, [load]);

  const s = data?.summary;
  const statCards = s ? [
    { label: 'Total Trips',   value: s.totalTrips || 0,                        icon: Route,      color: 'text-purple-600', bg: 'bg-purple-50' },
    { label: 'Total Distance',value: fmtKm(s.totalKm),                          icon: TrendingUp, color: 'text-blue-600',   bg: 'bg-blue-50'   },
    { label: 'Avg per Trip',  value: fmtKm(s.avgKm),                            icon: BarChart2,  color: 'text-green-600',  bg: 'bg-green-50'  },
    { label: 'Avg Duration',  value: fmtMin(s.avgDurationMin),                  icon: Clock,      color: 'text-orange-600', bg: 'bg-orange-50' },
  ] : [];

  return (
    <div className="p-5 space-y-6">

      {/* ── Filters ── */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 font-medium">From</label>
          <input type="date" value={from} onChange={e=>setFrom(e.target.value)} max={to}
            className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"/>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 font-medium">To</label>
          <input type="date" value={to} onChange={e=>setTo(e.target.value)} min={from} max={today}
            className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"/>
        </div>
        <select value={routeId} onChange={e=>setRouteId(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
          <option value="">All Routes</option>
          {routes.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-1.5 px-4 py-1.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-60">
          <RefreshCw size={13}/> {loading ? 'Loading…' : 'Apply'}
        </button>
      </div>

      {/* ── Active Trips Now ── */}
      {data?.activeTrips?.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse inline-block"/>
            Active Trips Right Now ({data.activeTrips.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.activeTrips.map(t=>(
              <div key={t.id} className="flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-xl">
                <Bus size={18} className="text-green-600 flex-shrink-0"/>
                <div>
                  <p className="text-sm font-semibold text-gray-800">{t.routeName||t.routeId}</p>
                  <p className="text-xs text-gray-500">
                    {t.tripType==='pickup'?'🟣 Pickup':'🟤 Drop'} &nbsp;•&nbsp;
                    {t.driverName||'Driver'} &nbsp;•&nbsp;
                    Running {fmtMin(t.runningMin)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Summary Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map(({label,value,icon:Icon,color,bg})=>(
          <div key={label} className="bg-white border border-gray-100 rounded-xl px-4 py-4 flex items-center gap-3 shadow-sm">
            <div className={`w-9 h-9 ${bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
              <Icon size={18} className={color}/>
            </div>
            <div>
              <p className="text-lg font-bold text-gray-800">{value}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Route-wise Performance ── */}
      {data?.routeStats?.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-gray-700 mb-3">Route-wise Performance</h3>
          <div className="overflow-x-auto rounded-xl border border-gray-100 shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Route','Trips','Total KM','Avg KM / Trip','Avg Duration','Active Days'].map(h=>(
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.routeStats.map(r=>(
                  <tr key={r.routeId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold text-gray-800">{r.routeName||r.routeId}</td>
                    <td className="px-4 py-3 text-gray-600">{r.totalTrips}</td>
                    <td className="px-4 py-3 text-gray-600">{fmtKm(r.totalKm)}</td>
                    <td className="px-4 py-3 text-gray-600">{fmtKm(r.avgKm)}</td>
                    <td className="px-4 py-3 text-gray-600">{fmtMin(r.avgDurationMin)}</td>
                    <td className="px-4 py-3 text-gray-600">{r.activeDays}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Day-wise Breakdown ── */}
      {data?.dayStats?.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-gray-700 mb-3">Day-wise Breakdown</h3>
          <div className="overflow-x-auto rounded-xl border border-gray-100 shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Date','Trips','Total KM','Avg Duration','Routes Active'].map(h=>(
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.dayStats.map(d=>(
                  <tr key={d.date} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">{fmtDate(d.date)}</td>
                    <td className="px-4 py-3 text-gray-600">{d.trips}</td>
                    <td className="px-4 py-3 text-gray-600">{fmtKm(d.totalKm)}</td>
                    <td className="px-4 py-3 text-gray-600">{fmtMin(d.avgDurationMin)}</td>
                    <td className="px-4 py-3 text-gray-600">{d.routes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Trip Log ── */}
      {data?.tripLogs?.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-gray-700 mb-3">Trip History</h3>
          <div className="overflow-x-auto rounded-xl border border-gray-100 shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Route','Type','Start','End','Duration','Distance','Status'].map(h=>(
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.tripLogs.map(t=>(
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold text-gray-800">{t.routeName||t.routeId}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${t.tripType==='pickup'?'bg-purple-100 text-purple-700':'bg-yellow-100 text-yellow-700'}`}>
                        {t.tripType==='pickup'?'🟣 Pickup':'🟤 Drop'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtTime(t.startTime)}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{t.endTime ? fmtTime(t.endTime) : '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{fmtMin(t.durationMin)}</td>
                    <td className="px-4 py-3 text-gray-600">{fmtKm(t.distanceKm)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${t.status==='active'?'bg-green-100 text-green-700':'bg-gray-100 text-gray-600'}`}>
                        {t.status==='active'?'Active':'Done'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Stop Arrival Analytics ── */}
      <div>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <h3 className="text-sm font-bold text-gray-700">Stop Arrival Timing</h3>
          <input type="date" value={stopDate} max={today}
            onChange={e=>{setStopDate(e.target.value);}}
            className="px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"/>
          <select value={stopRoute} onChange={e=>setStopRoute(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
            <option value="">All Routes</option>
            {routes.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        {stops.length === 0 ? (
          <div className="py-8 text-center text-gray-400 bg-gray-50 rounded-xl border border-gray-100">
            <MapPin size={28} className="mx-auto mb-2 opacity-30"/>
            <p className="text-sm">No stop arrival data for this date</p>
            <p className="text-xs mt-1">Data is recorded when driver marks stop arrivals in the app</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-100 shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Stop Name','Arrivals','Avg Arrival Time','Earliest','Latest'].map(h=>(
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {stops.map(s=>(
                  <tr key={s.stopName} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold text-gray-800">📍 {s.stopName}</td>
                    <td className="px-4 py-3 text-center text-gray-600">{s.arrivals}</td>
                    <td className="px-4 py-3 text-gray-600 font-medium">{s.avgArrivalTime||'—'}</td>
                    <td className="px-4 py-3 text-green-600">{s.earliest||'—'}</td>
                    <td className="px-4 py-3 text-red-500">{s.latest||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!data && !loading && (
        <div className="py-16 text-center text-gray-400">
          <BarChart2 size={40} className="mx-auto mb-3 opacity-30"/>
          <p>No trip data found for this period</p>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
const alertColors = {
  delay:        'bg-yellow-100 text-yellow-700 border-yellow-200',
  emergency:    'bg-red-100 text-red-700 border-red-200',
  breakdown:    'bg-orange-100 text-orange-700 border-orange-200',
  route_change: 'bg-blue-100 text-blue-700 border-blue-200',
};
const alertIcons = { delay:'⏱', emergency:'🚨', breakdown:'🔧', route_change:'🔄' };

export default function LiveTracking() {
  const { routes, vehicles, staff, students } = useApp();

  const [livePositions, setLivePositions] = useState([]);
  const [alerts, setAlerts]               = useState([]);
  const [notifLogs, setNotifLogs]         = useState([]);
  const [queueStats, setQueueStats]       = useState(null);
  const [selectedRoute, setSelectedRoute] = useState('all');
  const [tab, setTab]                     = useState('tracking');
  const [alertModal, setAlertModal]       = useState(false);
  const [lastRefresh, setLastRefresh]     = useState(null);
  const [autoRefresh, setAutoRefresh]     = useState(true);
  const timerRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const [pos, al, logs, stats] = await Promise.all([
        api.list('transport/live'),
        api.list('transport/alerts'),
        api.list('transport/notification-logs', { limit: 50 }),
        api.list('transport/queue-stats'),
      ]);
      setLivePositions(Array.isArray(pos) ? pos : []);
      setAlerts(Array.isArray(al) ? al : []);
      setNotifLogs(Array.isArray(logs) ? logs : []);
      setQueueStats(stats);
      setLastRefresh(new Date());
    } catch {}
  }, []);

  useEffect(() => { fetchAll(); return () => clearInterval(timerRef.current); }, [fetchAll]);
  useEffect(() => {
    clearInterval(timerRef.current);
    if (autoRefresh) timerRef.current = setInterval(fetchAll, REFRESH_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [autoRefresh, fetchAll]);

  const resolveAlert = async id => {
    await api.update('transport/alerts', `${id}/resolve`, {});
    setAlerts(a => a.filter(x => x.id !== id));
  };
  const sendAlert = async form => { await api.create('transport/alerts', form); fetchAll(); };

  const staleMinutes = 10;
  const onlineCount  = livePositions.filter(p => ageMin(p.recordedAt||p.recorded_at||'9999') < staleMinutes).length;
  const filteredPos  = selectedRoute === 'all' ? livePositions : livePositions.filter(p => p.routeId === selectedRoute);
  const todayLogs    = notifLogs.filter(l => new Date(l.sentAt).toDateString() === new Date().toDateString());

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Live Bus Tracking</h1>
          <p className="text-gray-500 text-sm mt-0.5">Real-time GPS · Parent alerts · Trip analytics</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {lastRefresh && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Clock size={12}/> {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button onClick={() => setAutoRefresh(v=>!v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-colors ${autoRefresh?'bg-green-50 border-green-200 text-green-700':'bg-gray-50 border-gray-200 text-gray-500'}`}>
            {autoRefresh ? <Wifi size={14}/> : <WifiOff size={14}/>}
            {autoRefresh ? 'Live' : 'Paused'}
          </button>
          <button onClick={fetchAll}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-gray-300 hover:bg-gray-50">
            <RefreshCw size={14}/> Refresh
          </button>
          <button onClick={() => setAlertModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
            <AlertTriangle size={16}/> Send Alert
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label:'Active Routes',       value:livePositions.length, icon:Bus,          color:'text-purple-600', bg:'bg-purple-50' },
          { label:'Buses Online Now',    value:onlineCount,          icon:Navigation,   color:'text-green-600',  bg:'bg-green-50'  },
          { label:'Active Alerts',       value:alerts.length,        icon:AlertTriangle,color:'text-red-600',    bg:'bg-red-50'    },
          { label:"Today's Alerts Sent", value:todayLogs.length,     icon:Bell,         color:'text-blue-600',   bg:'bg-blue-50'   },
        ].map(({label,value,icon:Icon,color,bg})=>(
          <div key={label} className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4 flex items-center gap-4">
            <div className={`w-10 h-10 ${bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
              <Icon size={20} className={color}/>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {queueStats && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-6 text-sm text-gray-600">
          <span className="font-semibold text-gray-700">Notification Queue:</span>
          <span>Queued: <b>{queueStats.queued}</b></span>
          <span>Workers: <b>{queueStats.active}</b></span>
          <span>Processed: <b>{queueStats.processed}</b></span>
          <span>Failed: <b>{queueStats.failed}</b></span>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex border-b border-gray-100 overflow-x-auto">
          {[
            { key:'tracking',  label:'📍 Live Positions' },
            { key:'analytics', label:'📊 Analytics' },
            { key:'alerts',    label:`🚨 Alerts${alerts.length?` (${alerts.length})`:''}` },
            { key:'logs',      label:'🔔 Notification Logs' },
          ].map(t=>(
            <button key={t.key} onClick={()=>setTab(t.key)}
              className={`px-5 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${tab===t.key?'border-purple-600 text-purple-600':'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {t.label}
            </button>
          ))}
          {tab==='tracking' && (
            <div className="ml-auto flex items-center pr-4">
              <select value={selectedRoute} onChange={e=>setSelectedRoute(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
                <option value="all">All Routes</option>
                {routes.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          )}
        </div>

        {/* Live Positions */}
        {tab==='tracking' && (
          <div>
            <BusMap positions={livePositions} selectedRoute={selectedRoute}/>
            <div className="divide-y divide-gray-50">
              {filteredPos.length===0 ? (
                <div className="py-12 text-center text-gray-400">
                  <Bus size={40} className="mx-auto mb-3 opacity-30"/>
                  <p className="font-medium">No live bus data</p>
                  <p className="text-sm mt-1">Drivers need to share location from the mobile app</p>
                </div>
              ) : filteredPos.map(pos=>{
                const route   = routes.find(r=>r.id===pos.routeId);
                const vehicle = vehicles.find(v=>v.id===route?.vehicleId);
                const age     = ageMin(pos.recordedAt||pos.recorded_at||'9999');
                const online  = age < staleMinutes;
                const routeStudents = students.filter(s=>s.transportRouteId===pos.routeId&&s.status==='active');
                return (
                  <div key={pos.routeId} className="px-5 py-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Bus size={20} className="text-purple-600"/>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-800">{pos.routeName||pos.routeId}</span>
                            <span className={`inline-block w-2.5 h-2.5 rounded-full ${online?'bg-green-500 animate-pulse':'bg-gray-300'}`}/>
                            <span className={`text-xs ${online?'text-green-600':'text-gray-400'}`}>
                              {online?'Live':`${age}m ago`}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                            <span>🚗 {vehicle?.registrationNumber||'No vehicle'}</span>
                            {pos.driverName && <span>👤 {pos.driverName}</span>}
                            <span>👨‍🎓 {routeStudents.length} students</span>
                            {pos.speed>0 && <span>💨 {(pos.speed*3.6).toFixed(0)} km/h</span>}
                          </div>
                          <div className="flex items-center gap-1.5 mt-1.5 text-xs text-gray-400">
                            <MapPin size={11}/>
                            <span>{parseFloat(pos.latitude).toFixed(6)}, {parseFloat(pos.longitude).toFixed(6)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-xs text-gray-400">{formatDateTime(pos.recordedAt||pos.recorded_at)}</span>
                        <a href={`https://www.google.com/maps?q=${pos.latitude},${pos.longitude}`}
                          target="_blank" rel="noreferrer"
                          className="text-xs text-purple-600 hover:underline flex items-center gap-1">
                          <MapPin size={11}/> View on Map
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Analytics */}
        {tab==='analytics' && <AnalyticsTab routes={routes}/>}

        {/* Alerts */}
        {tab==='alerts' && (
          <div className="divide-y divide-gray-50">
            {alerts.length===0 ? (
              <div className="py-16 text-center text-gray-400">
                <CheckCircle size={40} className="mx-auto mb-3 opacity-30"/>
                <p className="font-medium">No active alerts</p>
              </div>
            ) : alerts.map(a=>(
              <div key={a.id} className={`px-5 py-4 flex items-start justify-between border-l-4 ${alertColors[a.alertType]||'bg-gray-50 border-gray-300'}`}>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span>{alertIcons[a.alertType]||'⚠️'}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${alertColors[a.alertType]}`}>
                      {a.alertType?.toUpperCase()}
                    </span>
                    <span className="text-sm font-medium text-gray-700">{a.routeName||a.routeId}</span>
                  </div>
                  <p className="text-sm text-gray-700">{a.message}</p>
                  <p className="text-xs text-gray-400 mt-1">{formatDateTime(a.createdAt)}</p>
                </div>
                <button onClick={()=>resolveAlert(a.id)}
                  className="ml-4 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-lg flex-shrink-0">
                  ✓ Resolve
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Notification Logs */}
        {tab==='logs' && (
          <div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Time','Student','Route','Type','ETA','Distance','Tokens','Message'].map(h=>(
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {notifLogs.length===0 ? (
                  <tr><td colSpan={8} className="py-12 text-center text-gray-400">No notifications sent yet</td></tr>
                ) : notifLogs.map(l=>(
                  <tr key={l.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDateTime(l.sentAt)}</td>
                    <td className="px-4 py-3 font-medium text-gray-800">{l.studentName||l.studentId}</td>
                    <td className="px-4 py-3 text-gray-600">{l.routeName||l.routeId}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${alertColors[l.notificationType]||'bg-blue-100 text-blue-700'}`}>
                        {l.notificationType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{l.etaMinutes!=null?`${l.etaMinutes} min`:'—'}</td>
                    <td className="px-4 py-3 text-gray-600">{l.distanceMeters!=null?`${l.distanceMeters}m`:'—'}</td>
                    <td className="px-4 py-3 text-center text-gray-600">{l.fcmTokensCount}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 max-w-xs truncate" title={l.message}>{l.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {alertModal && <AlertModal routes={routes} onSend={sendAlert} onClose={()=>setAlertModal(false)}/>}
    </div>
  );
}
